import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { concatMap, take } from 'rxjs/operators';

export type HasUnsaveChangesChecker = () => Observable<boolean> | boolean;

@Injectable({ providedIn: 'root' })
export class UnsaveChangesService {
	protected checkers$ = new BehaviorSubject<HasUnsaveChangesChecker[]>([]);

	get routerCheckers() {
		return this.checkers$.value;
	}

	get hasRouterCheckers() {
		return this.routerCheckers.length > 0;
	}

	addRouterChecker(...checkers: HasUnsaveChangesChecker[]) {
		const nextCheckers = [...this.routerCheckers, ...checkers];
		this.checkers$.next(nextCheckers);
	}

	removeRouterChecker(...checkers: HasUnsaveChangesChecker[]) {
		const nextCheckers = this.routerCheckers.filter(checker => !checkers.includes(checker));
		this.checkers$.next(nextCheckers);
	}

	hasUnsavedChanges(): Observable<boolean> {
		if (!this.routerCheckers?.length) {
			return of(false);
		}
		const checkers$ = this.routerCheckers.map(checker => {
			const result = checker();
			if (result instanceof Observable) {
				return result.pipe(take(1));
			} else {
				return of(result);
			}
		});
		const check$ = checkers$.reduce((prevChecker$, checker$) =>
			prevChecker$.pipe(
				concatMap(result => {
					if (result) {
						return of(true);
					}
					return checker$;
				})
			)
		);
		return check$;
	}

	confirmUnsave() {
		return new Observable<boolean>(observer => {
			const result = this.confirmUnSaveSync();
			observer.next(result);
			observer.complete();
		});
	}

	confirmUnSaveSync() {
		// TODO: localize
		return confirm('У вас есть несохраненные данные, вы уверены, что хотите их отменить?');
	}

	checkAndConfirm() {
		return this.hasUnsavedChanges().pipe(
			concatMap(hasUnsaved => (hasUnsaved ? this.confirmUnsave() : of(true))),
			take(1)
		);
	}
}
